import gsap from 'gsap';
import appStore from '../utils/state';

export default class MobileMenuButton extends HTMLElement {
  private button: HTMLButtonElement | null = null;

  private onOpenTl: gsap.core.Timeline | null = null;
  private onCloseTl: gsap.core.Timeline | null = null;

  private isOpen: boolean = false;

  private openLabel: string | null = null;
  private closeLabel: string | null = null;

  private menuIcon: HTMLElement | null = null;
  private closeIcon: HTMLElement | null = null;

  private unsubscribeFromMainNavToggle: (() => void) | null = null;

  private onClickBound = this.onClick.bind(this);
  private onMainNavToggleBound = this.onMainNavToggle.bind(this);

  constructor() {
    super();
  }

  connectedCallback(): void {
    this.isOpen = this.dataset.isOpen === 'true';
    this.openLabel = this.dataset.openLabel || null;
    this.closeLabel = this.dataset.closeLabel || null;

    this.button = this.querySelector('[data-mobile-menu-button-ref="button"]');

    this.menuIcon = this.querySelector('[data-mobile-menu-button-ref="menu-icon"]');
    this.closeIcon = this.querySelector('[data-mobile-menu-button-ref="close-icon"]');

    this.addEventListeners();
    this.subscribe();
  }

  addEventListeners(): void {
    if (this.button) {
      this.button.addEventListener('click', this.onClickBound);
    }
  }

  removeEventListeners(): void {
    if (this.button) {
      this.button.removeEventListener('click', this.onClickBound);
    }
  }

  subscribe(): void {
    this.unsubscribeFromMainNavToggle = appStore.subscribeKey('isMainNavOpen', this.onMainNavToggleBound);
  }

  unsubscribe(): void {
    if (this.unsubscribeFromMainNavToggle) {
      this.unsubscribeFromMainNavToggle();
      this.unsubscribeFromMainNavToggle = null;
    }
  }

  onClick(): void {
    if (this.isOpen) {
      appStore.actions.closeMainNav();
    } else {
      appStore.actions.openMainNav('0');
    }
  }

  onMainNavToggle(): void {
    this.isOpen = appStore.getState().isMainNavOpen;
    const activeMainNavPanelId = appStore.getState().activeMainNavPanelId;

    if (this.button) {
      this.button.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');

      if (activeMainNavPanelId !== null) {
        this.button.setAttribute('aria-controls', `main-nav-panel-dialog-${activeMainNavPanelId}`);
      }
    }

    if (this.isOpen) {
      this.onOpen();
    } else {
      this.onClose();
    }
  }

  onOpen(): void {
    if (this.button && this.closeLabel) {
      this.button.setAttribute('aria-label', this.closeLabel);
    }

    if (this.menuIcon) {
      this.menuIcon.classList.remove('block');
      this.menuIcon.classList.add('hidden');
    }
    if (this.closeIcon) {
      this.closeIcon.classList.remove('hidden');
      this.closeIcon.classList.add('block');
    }
  }

  onClose(): void {
    if (this.button && this.openLabel) {
      this.button.setAttribute('aria-label', this.openLabel);
    }

    if (this.menuIcon) {
      this.menuIcon.classList.remove('hidden');
      this.menuIcon.classList.add('block');
    }
    if (this.closeIcon) {
      this.closeIcon.classList.add('hidden');
      this.closeIcon.classList.remove('block');
    }
  }

  disconnectedCallback(): void {
    this.unsubscribe();
    this.removeEventListeners();

    if (this.onOpenTl) {
      this.onOpenTl.kill();
    }
    if (this.onCloseTl) {
      this.onCloseTl.kill();
    }
  }
}

customElements.define('mobile-menu-button', MobileMenuButton);
